import type {
  ColumnSetting,
  Field,
  FieldBlock,
  FieldBlockSettingsBookings,
  FieldBlockSettingsBookingsInput,
  FieldBlockSettingsProductPrice,
  FieldCreate,
  FieldProxy,
  FieldProxyCreate,
  LayoutColumn,
  LayoutSection,
  LayoutSectionCreate,
  ListFieldValue,
  Module,
  TemplateCreate,
  TemplateKindCreate,
  TemplateKindProductPriceGroupIds,
  TemplateOutput,
  TemplateSectionGroupInfo,
  TemplateUpdate,
} from "@scayla/api-client-fetch";
import {
  FieldBlockType,
  FieldContentType,
  FieldKind,
  FieldType,
  FileFolderType,
  LayoutAlignVertical,
  LayoutRowType,
  LayoutSectionGroupKind,
  LayoutSectionStatus,
  LayoutSectionType,
  NotesType,
  ProductPriceType,
} from "@scayla/api-client-fetch";
import type { TabItem } from "@scayla/components";
import { SCAConfirmationDialog } from "@scayla/components";
import type { InjectionKey, Ref } from "vue";
import { ref } from "vue";
import { useModalStore } from "vue-haystack";

import {
  ACCOUNTING_BOOKING_COLUMN_DEFAULT_WIDTHS,
  DEFAULT_ACCOUNTING_BOOKING_COLUMN_ORDER,
  DEFAULT_ACCOUNTING_BOOKING_VISIBLE_COLUMNS,
} from "@/constants/accountingBooking";
import { PRODUCT_ACCOUNTING_FIELD_SLUGS, PRODUCT_ACCOUNTING_SLUGS_BY_PRICE_TYPE } from "@/constants/productAccounting";
import type { ProductPriceColumn } from "@/constants/productPrice";
import {
  DEFAULT_PRODUCT_PRICE_COLUMN_ORDER,
  DEFAULT_PRODUCT_PRICE_VISIBLE_COLUMNS,
  getDefaultProductPriceColumnSettings,
  PRODUCT_PRICE_COLUMN_DEFAULT_WIDTHS,
} from "@/constants/productPrice";
import { $t } from "@/i18n";
import {
  newBookingsFieldBlockSettingsInput,
  newFoldersFieldBlockSettingsInput,
  newNotesFieldBlockSettingsInput,
  newProductPriceFieldBlockSettingsInput,
} from "@/utils/fieldBlockSettingsNormalize";
import type { FieldCarryingSection } from "@/utils/layoutTree";
import { collectSectionFields } from "@/utils/layoutTree";
import { isPrivateNotesType, isPublicNotesType } from "@/utils/notesTabPolicy";
import type { FieldBlockForm } from "@/views/settings/template/TemplateCollection.vue";
import type { FieldProxyForm } from "@/views/settings/template/TemplateField.vue";
import type { LayoutSectionForm } from "@/views/settings/template/TemplateSection.vue";

import type { BandForm } from "./bandUtils";

export type TemplateForm = (TemplateCreate | TemplateUpdate) & {
  sections: LayoutSectionForm[];
  moduleImplicitRelationsVisibility?: Module[];
  sectionGroups?: TemplateSectionGroupInfo[];
};

export interface KindSelectionPayload {
  id?: string;
  uiId: string | null;
  index: number;
}

export interface KindSwitchRequest {
  id?: string;
  uiId?: string;
  index: number;
}

/**
 * Field slug together with its field content type (kind).
 * Used for cross-field slug validation in templates.
 */
export interface SlugEntry {
  slug: string;
  kind: FieldContentType | null | undefined;
  inSidebar: boolean;
}

/**
 * Returns true if adding/editing a field with the given slug and kind
 * would conflict with existing slug entries.
 *
 * Rules:
 *   - Same slug + same kind → OK (duplicates allowed)
 *   - Same slug + different kind → conflict
 */
export function findConflictSlug(
  existing: SlugEntry[],
  slug: string,
  kind: FieldContentType | null | undefined,
  originalSlug?: string,
  originalKind?: FieldContentType | null,
): SlugEntry | undefined {
  let excludedCurrent = false;
  const others = existing.filter((entry) => {
    if (!excludedCurrent && entry.slug === originalSlug && entry.kind === originalKind) {
      excludedCurrent = true;
      return false;
    }
    return true;
  });
  return others.find((entry) => entry.slug === slug && entry.kind !== kind);
}

/**
 * Scans a list of slug entries for internal conflicts:
 * the first entry whose slug already appears in the list with a different kind.
 * Returns undefined when every slug is used consistently.
 */
export function findSlugConflictInList(entries: SlugEntry[]): SlugEntry | undefined {
  const seen = new Map<string, FieldContentType | null | undefined>();
  for (const entry of entries) {
    if (seen.has(entry.slug)) {
      if (seen.get(entry.slug) !== entry.kind) {
        return entry;
      }
    } else {
      seen.set(entry.slug, entry.kind);
    }
  }
  return undefined;
}

function fieldProxyToSlugEntries(f: FieldProxyCreate & FieldProxyForm, inSidebar: boolean): SlugEntry[] {
  if (f.field) {
    return [{ slug: f.slug, kind: f.field.contentType, inSidebar }];
  }
  if (f.fieldGroup) {
    return f.fieldGroup.rows
      .flatMap((row) => row.fields)
      .map((gf) => ({ slug: gf.slug, kind: gf.field.contentType, inSidebar }));
  }

  return [{ slug: f.slug, kind: undefined, inSidebar }];
}

/**
 * Collects all slugs items from a sections structure
 */
export function collectSectionSlugs(sections: SectionItem[], inSidebar: boolean): SlugEntry[] {
  return collectSectionFields(sections as FieldCarryingSection<FieldProxyCreate & FieldProxyForm>[]).flatMap((f) =>
    fieldProxyToSlugEntries(f, inSidebar),
  );
}

export type SectionItem = TemplateForm["sections"][number];

export const DEFAULT_SECTION_TYPES = [
  LayoutSectionType.NOTES,
  LayoutSectionType.BOOKINGS,
  LayoutSectionType.FILES,
  LayoutSectionType.RELATIONS,
] as LayoutSectionType[];

export function isDefaultSectionType(type: string | null | undefined): boolean {
  return DEFAULT_SECTION_TYPES.includes(type as (typeof DEFAULT_SECTION_TYPES)[number]);
}

export const KIND_FIELD_SLUG = "_kind";

export function createDefaultInnerField(organisationId: string, withDefaultOption = true): FieldCreate {
  const defaultOption = {
    kind: "list",
    value: {
      label: $t("common.label.default"),
      value: $t("common.label.default").toLowerCase(),
    },
  } as ListFieldValue;

  return {
    contentType: FieldContentType.LIST,
    type: FieldType.INNER_TEMPLATE_FIELD,
    organizationId: organisationId,
    label: "Art",
    showAsTableColumn: false,
    defaultSlug: KIND_FIELD_SLUG,
    isReadOnly: false,
    allowMultipleOptions: false,
    options: withDefaultOption ? [defaultOption] : [],
    defaultValue: withDefaultOption ? defaultOption : undefined,
    kind: FieldKind.SINGLE_SELECTION,
  };
}

export function createDefaultKind({
  hasSections = true,
  hasBookings = false,
  hasRelations = false,
  hasProductAccounting = false,
}): TemplateKindCreate {
  return {
    label: $t("common.label.default"),
    value: $t("common.label.default").toLowerCase(),
    ordering: 0,
    isDefault: true,
    sections: hasSections ? createDefaultSections({ hasBookings, hasRelations, hasProductAccounting }) : undefined,
  };
}

export function createDefaultSections({
  hasBookings = false,
  hasRelations = false,
  hasProductAccounting = false,
}): LayoutSectionCreate[] {
  const sections = [];
  let index = 0;
  sections.push({
    label: $t("common.label.header"),
    fieldCollections: [
      {
        columns: [],
        bands: [{ columns: [{ rows: [{ fields: [], ordering: 0 }], ordering: 0 }], ordering: 0 }],
        ordering: 0,
      },
    ],
    inSidebar: false,
    simple: false,
    ordering: index,
    type: LayoutSectionType.HEAD,
    alignVertical: LayoutAlignVertical.TOP,
    labelSize: 52,
  });
  index++;
  if (hasProductAccounting) {
    sections.push({
      label: $t("common.label.sectionBody"),
      fieldCollections: getDefaultProductBodyFieldCollections(),
      inSidebar: false,
      simple: false,
      ordering: index,
      type: LayoutSectionType.BODY,
      status: LayoutSectionStatus.ACTIVE,
      labelSize: 128,
    });
    index++;
  }
  sections.push({
    label: $t("common.label.notes"),
    fieldCollections: getDefaultNotesFieldCollection(),
    inSidebar: false,
    simple: false,
    ordering: index,
    type: LayoutSectionType.NOTES,
    status: LayoutSectionStatus.ACTIVE,
  });
  index++;
  if (hasBookings) {
    sections.push({
      label: $t("common.label.accountingTable"),
      fieldCollections: getDefaultBookingsFieldCollection(),
      inSidebar: false,
      simple: false,
      ordering: index,
      type: LayoutSectionType.BOOKINGS,
      status: LayoutSectionStatus.HIDDEN,
    });
    index++;
  }
  sections.push({
    label: $t("common.label.filesFolders"),
    fieldCollections: getDefaultFilesFieldCollection(),
    inSidebar: false,
    simple: false,
    ordering: index,
    type: LayoutSectionType.FILES,
    status: LayoutSectionStatus.ACTIVE,
  });
  index++;
  if (hasRelations) {
    sections.push({
      label: $t("common.label.dossierConnections"),
      fieldCollections: [],
      inSidebar: false,
      simple: false,
      ordering: index,
      type: LayoutSectionType.RELATIONS,
      status: LayoutSectionStatus.ACTIVE,
    });
    index++;
  }
  return sections;
}

export function createDefaultPriceSections(field: PriceTypeField): SectionItem[] {
  const priceType = PRODUCT_PRICE_SECTION_TYPE_BY_FIELD[field];
  return [
    {
      label: getTemplateSectionLabel(priceType),
      type: priceType,
      ordering: 0,
      inSidebar: false,
      simple: false,
      status: LayoutSectionStatus.ACTIVE,
      fieldCollections: getDefaultProductPriceFieldCollection(PRICE_TYPE_BY_FIELD[field]),
      labelSize: 128,
    },
    {
      label: $t("common.label.notes"),
      type: LayoutSectionType.NOTES,
      ordering: 1,
      inSidebar: false,
      simple: false,
      status: LayoutSectionStatus.ACTIVE,
      fieldCollections: getDefaultPriceNotesFieldCollection(),
    },
    {
      label: $t("common.label.filesFolders"),
      type: LayoutSectionType.FILES,
      ordering: 2,
      inSidebar: false,
      simple: false,
      status: LayoutSectionStatus.ACTIVE,
      fieldCollections: getDefaultPriceFilesFieldCollection(),
    },
  ];
}

export function ensurePriceGroupSections(
  priceGroups: Record<string, SectionItem[]>,
  priceGroupIds: TemplateKindProductPriceGroupIds | undefined,
  enabled: Partial<Record<PriceTypeField, boolean>>,
): void {
  for (const field of PRICE_FLAG_FIELDS) {
    if (!enabled[field]) continue;
    const groupId = priceGroupIds?.[PRICE_GROUP_ID_KEY_BY_FIELD[field]];
    if (!groupId) continue;

    const existing = priceGroups[groupId];
    if (existing?.length) {
      fillEmptyPriceSubSections(existing);
      continue;
    }

    const pending = priceGroups[field];
    const sections = pending?.length ? pending : createDefaultPriceSections(field);
    fillEmptyPriceSubSections(sections);
    priceGroups[groupId] = sections.map((section) => ({ ...section, groupId }));
  }

  for (const field of PRICE_FLAG_FIELDS) {
    delete priceGroups[field];
  }
}

/**
 * Adds any missing block to a price tab's files section, and rewrites a `DEFAULT` one to `CUSTOM`:
 * `DEFAULT` resolves to the record root, which every other tab already shows.
 */
function ensurePriceFilesFieldCollections(section: SectionItem): void {
  /** Only the fields this normalisation touches; the section's own type comes from a .vue module. */
  interface FilesBlock {
    ordering?: number;
    columns?: ColumnSetting[];
    settings?: { type?: FileFolderType; columns?: ColumnSetting[] };
    tab?: { ordering?: number; label?: string };
  }

  const collections = (section.fieldCollections ?? []) as unknown as FilesBlock[];
  if (collections.length === 0) {
    section.fieldCollections = getDefaultPriceFilesFieldCollection() as never;
    return;
  }

  for (const block of collections) {
    if (block.settings?.type === FileFolderType.DEFAULT) {
      block.settings = newFoldersFieldBlockSettingsInput(FileFolderType.CUSTOM, block.columns ?? []);
    }
  }

  const defaults = getDefaultPriceFilesFieldCollection() as unknown as FilesBlock[];
  const missing = defaults.filter((def) => !collections.some((block) => block.settings?.type === def.settings?.type));
  if (missing.length === 0) return;

  let ordering = collections.reduce((max, block) => Math.max(max, block.ordering ?? 0), 0);
  for (const def of missing) {
    ordering += 1;
    collections.push({ ...def, ordering, tab: { ...def.tab, ordering } });
  }
  section.fieldCollections = collections as never;
}

/**
 * Notes counterpart of `ensurePriceFilesFieldCollections`. Brings a price tab's notes section to the
 * expected pair of blocks: a `CUSTOM_PUBLIC` and a `CUSTOM_PRIVATE` tab. Runs on every load, so a
 * group saved with plain `PUBLIC`/`PRIVATE` notes — which key globally and would be shared with the
 * main tab — is normalised to the `CUSTOM_*` variants, keeping any custom tabs and their content.
 */
function ensurePriceNotesFieldCollections(section: SectionItem): void {
  /** Only the fields this normalisation touches; the section's own type comes from a .vue module. */
  interface NotesBlock {
    ordering?: number;
    localKey?: string;
    settings?: { type?: NotesType; note?: string };
    tab?: { ordering?: number; label?: string };
  }

  const collections = (section.fieldCollections ?? []) as unknown as NotesBlock[];
  if (collections.length === 0) {
    section.fieldCollections = getDefaultPriceNotesFieldCollection() as never;
    return;
  }

  for (const block of collections) {
    const note = block.settings?.note ?? "";
    if (block.settings?.type === NotesType.PUBLIC) {
      block.settings = newNotesFieldBlockSettingsInput(NotesType.CUSTOM_PUBLIC, note);
    } else if (block.settings?.type === NotesType.PRIVATE) {
      block.settings = newNotesFieldBlockSettingsInput(NotesType.CUSTOM_PRIVATE, note);
    }
  }

  const defaults = getDefaultPriceNotesFieldCollection() as unknown as NotesBlock[];
  const missing = defaults.filter((def) => !collections.some((block) => block.settings?.type === def.settings?.type));
  if (missing.length === 0) return;

  let ordering = collections.reduce((max, block) => Math.max(max, block.ordering ?? 0), 0);
  for (const def of missing) {
    ordering += 1;
    collections.push({ ...def, ordering, tab: { ...def.tab, ordering } });
  }
  section.fieldCollections = collections as never;
}

function fillEmptyPriceSubSections(sections: SectionItem[]): void {
  for (const section of sections) {
    const sectionType = section.type as LayoutSectionType | undefined;
    const priceType = sectionType ? PRODUCT_PRICE_TYPE_BY_SECTION_TYPE.get(sectionType) : undefined;
    if (priceType) {
      ensureProductPriceFieldCollection(section, priceType);
      continue;
    }
    if (section.type === LayoutSectionType.FILES) {
      ensurePriceFilesFieldCollections(section);
      continue;
    }
    if (section.type === LayoutSectionType.NOTES) {
      ensurePriceNotesFieldCollections(section);
      continue;
    }
  }
}

export function getDefaultProductPriceFieldCollection(priceType: ProductPriceType): FieldBlockForm[] {
  return [
    {
      columns: [],
      ordering: 0,
      type: FieldBlockType.PRODUCT_PRICE,
      localKey: crypto.randomUUID(),
      settings: newProductPriceFieldBlockSettingsInput(
        getDefaultProductPriceColumnSettings(priceType),
        DEFAULT_PRODUCT_PRICE_SHOW_FILTER_ROW,
      ),
    },
  ];
}

/**
 * Keep every known column present in the stored settings: a template saved before a column existed
 * would otherwise hide it forever, since the grid only renders what the settings list.
 */
export function mergeProductPriceColumns(existing: ColumnSetting[], priceType: ProductPriceType): ColumnSetting[] {
  const bySlug = new Map(existing.map((c) => [c.fieldSlug, c]));
  if (DEFAULT_PRODUCT_PRICE_COLUMN_ORDER.every((slug) => bySlug.has(slug))) return existing;

  const visibleDefaults = new Set<string>(DEFAULT_PRODUCT_PRICE_VISIBLE_COLUMNS[priceType]);
  const known = new Set<string>(DEFAULT_PRODUCT_PRICE_COLUMN_ORDER);

  const storedOrder = [...existing]
    .sort((a, b) => a.order - b.order)
    .map((c) => c.fieldSlug)
    .filter((slug): slug is ProductPriceColumn => known.has(slug));
  const missing = DEFAULT_PRODUCT_PRICE_COLUMN_ORDER.filter((slug) => !bySlug.has(slug));

  return [...storedOrder, ...missing].map((slug, idx) => {
    const found = bySlug.get(slug);
    if (found) return { ...found, order: idx };
    return {
      fieldSlug: slug,
      isVisible: visibleDefaults.has(slug),
      width: PRODUCT_PRICE_COLUMN_DEFAULT_WIDTHS[slug],
      order: idx,
    };
  });
}

function readProductPriceSettings(section: SectionItem | undefined): FieldBlockSettingsProductPrice | undefined {
  const fc = section?.fieldCollections?.[0] as { settings?: unknown } | undefined;
  return fc?.settings as FieldBlockSettingsProductPrice | undefined;
}

function extractProductPriceColumns(section: SectionItem | undefined): ColumnSetting[] {
  const columns = readProductPriceSettings(section)?.columns;
  return Array.isArray(columns) ? columns : [];
}

function ensureProductPriceFieldCollection(section: SectionItem, priceType: ProductPriceType): void {
  const fc = section.fieldCollections?.[0];
  if (!fc) {
    section.fieldCollections = getDefaultProductPriceFieldCollection(priceType) as never;
    return;
  }

  const existing = extractProductPriceColumns(section);
  const merged = mergeProductPriceColumns(existing, priceType);
  if (merged === existing) return;

  const showFilterRow = readProductPriceSettings(section)?.showFilterRow;
  fc.settings = newProductPriceFieldBlockSettingsInput(
    merged,
    typeof showFilterRow === "boolean" ? showFilterRow : DEFAULT_PRODUCT_PRICE_SHOW_FILTER_ROW,
  );
}

/** Column settings of a price section, falling back to the price type's defaults. */
export function getProductPriceSectionColumns(
  section: SectionItem | undefined,
  priceType: ProductPriceType,
): ColumnSetting[] {
  const cols = extractProductPriceColumns(section);
  return cols.length > 0 ? mergeProductPriceColumns(cols, priceType) : getDefaultProductPriceColumnSettings(priceType);
}

/** `showFilterRow` of a price section (default true). */
export function getProductPriceSectionShowFilterRow(section: SectionItem | undefined): boolean {
  const showFilterRow = readProductPriceSettings(section)?.showFilterRow;
  return typeof showFilterRow === "boolean" ? showFilterRow : DEFAULT_PRODUCT_PRICE_SHOW_FILTER_ROW;
}

interface KindLike {
  label?: string;
  value?: string;
}

function validateOptions(
  items: KindLike[],
  getLabel: (item: KindLike) => string,
  getValue: (item: KindLike) => string,
): string | null {
  if (!items || items.length === 0) return null;

  const hasEmpty = items.some((item) => {
    return getLabel(item) === "" || getValue(item) === "";
  });

  if (hasEmpty) {
    return $t("common.validation.labelOption");
  }

  const values = items.map(getValue);
  if (new Set(values).size !== values.length) {
    return $t("common.label.optionUniqueValue");
  }

  return null;
}

export function validateKinds(kinds: TemplateForm["kinds"]): string | null {
  return validateOptions(
    (kinds ?? []) as KindLike[],
    (k) => (k.label ?? "").trim(),
    (k) => (k.value ?? "").trim(),
  );
}

export function validateInnerTemplateFields(field: FieldCreate): string | null {
  if (!field || !Array.isArray(field.options)) return null;

  const listOptions = field.options.filter((opt) => opt.kind === "list").map((opt) => opt.value);

  return validateOptions(
    listOptions,
    (v) => (v.label ?? "").trim(),
    (v) => (v.value ?? "").trim(),
  );
}

export function getTemplateSectionLabel(type: LayoutSectionType | undefined) {
  switch (type) {
    case LayoutSectionType.HEAD:
      return $t("common.label.sectionHead");
    case LayoutSectionType.BODY:
      return $t("common.label.sectionBody");
    case LayoutSectionType.NOTES:
      return $t("common.label.sectionNote");
    case LayoutSectionType.BOOKINGS:
      return $t("common.label.sectionAccounting");
    case LayoutSectionType.FILES:
      return $t("common.label.sectionFilesFolders");
    case LayoutSectionType.RELATIONS:
      return $t("common.label.sectionConnections");
    case LayoutSectionType.SIDE_BAR:
      return $t("common.label.section");
    case LayoutSectionType.PRODUCT_PRICE_SALE:
    case LayoutSectionType.PRODUCT_PRICE_PURCHASE:
    case LayoutSectionType.PRODUCT_PRICE_RENT:
    case LayoutSectionType.PRODUCT_PRICE_RENTOUT:
      return $t("common.label.sectionPrice");
    default:
      return "";
  }
}

export type PriceTypeField = "saleEnabled" | "purchaseEnabled" | "rentEnabled" | "rentoutEnabled";

interface PriceTypeMeta {
  field: PriceTypeField;
  sectionType: LayoutSectionType;
  priceType: ProductPriceType;
  groupKind: LayoutSectionGroupKind;
  groupIdKey: keyof TemplateKindProductPriceGroupIds;
  labelKey: string;
}

const PRICE_TYPES: readonly PriceTypeMeta[] = [
  {
    field: "saleEnabled",
    sectionType: LayoutSectionType.PRODUCT_PRICE_SALE,
    priceType: ProductPriceType.SALES,
    groupKind: LayoutSectionGroupKind.SALE,
    groupIdKey: "saleGroupId",
    labelKey: "common.label.productPriceSale",
  },
  {
    field: "purchaseEnabled",
    sectionType: LayoutSectionType.PRODUCT_PRICE_PURCHASE,
    priceType: ProductPriceType.PURCHASE,
    groupKind: LayoutSectionGroupKind.PURCHASE,
    groupIdKey: "purchaseGroupId",
    labelKey: "common.label.productPricePurchase",
  },
  {
    field: "rentEnabled",
    sectionType: LayoutSectionType.PRODUCT_PRICE_RENT,
    priceType: ProductPriceType.RENT,
    groupKind: LayoutSectionGroupKind.RENT,
    groupIdKey: "rentGroupId",
    labelKey: "common.label.productPriceRent",
  },
  {
    field: "rentoutEnabled",
    sectionType: LayoutSectionType.PRODUCT_PRICE_RENTOUT,
    priceType: ProductPriceType.RENT_OUT,
    groupKind: LayoutSectionGroupKind.RENTOUT,
    groupIdKey: "rentoutGroupId",
    labelKey: "common.label.productPriceRentOut",
  },
];

function priceTypeRecord<V>(pick: (meta: PriceTypeMeta) => V): Record<PriceTypeField, V> {
  return Object.fromEntries(PRICE_TYPES.map((meta) => [meta.field, pick(meta)])) as Record<PriceTypeField, V>;
}

export const PRICE_FLAG_FIELDS: PriceTypeField[] = PRICE_TYPES.map((meta) => meta.field);

export const SELECTED_PRICE_TAB_KEY: InjectionKey<Ref<PriceTypeField | null>> = Symbol("selectedPriceTab");

export const PRODUCT_PRICE_SECTION_TYPE_BY_FIELD = priceTypeRecord((meta) => meta.sectionType);

export const PRODUCT_PRICE_SECTION_TYPES = PRICE_TYPES.map((meta) => meta.sectionType);

/** Price section type -> the ProductPriceType its table shows. */
export const PRODUCT_PRICE_TYPE_BY_SECTION_TYPE = new Map<LayoutSectionType, ProductPriceType>(
  PRICE_TYPES.map((meta) => [meta.sectionType, meta.priceType]),
);

const PRICE_TYPE_BY_FIELD = priceTypeRecord((meta) => meta.priceType);

/** Price tabs of a template, in descriptor order, limited to the enabled toggles. */
export function enabledPriceTypes(
  template:
    | {
        saleEnabled?: boolean;
        purchaseEnabled?: boolean;
        rentEnabled?: boolean;
        rentoutEnabled?: boolean;
      }
    | undefined,
): { field: PriceTypeField; priceType: ProductPriceType; sectionType: LayoutSectionType; label: string }[] {
  if (!template) return [];
  return PRICE_TYPES.filter((meta) => !!template[meta.field]).map((meta) => ({
    field: meta.field,
    priceType: meta.priceType,
    sectionType: meta.sectionType,
    label: $t(meta.labelKey),
  }));
}

/** Group id of one price type on a kind, if the backend has provisioned it. */
export function priceGroupIdForType(
  groupIds: TemplateKindProductPriceGroupIds | undefined,
  priceType: ProductPriceType,
): string | undefined {
  const meta = PRICE_TYPES.find((m) => m.priceType === priceType);
  return meta ? groupIds?.[meta.groupIdKey] : undefined;
}

const PRICE_GROUP_KIND_BY_FIELD = priceTypeRecord((meta) => meta.groupKind);

export const PRICE_GROUP_ID_KEY_BY_FIELD = priceTypeRecord((meta) => meta.groupIdKey);

export function resolvePriceGroupId(
  sectionGroups: TemplateSectionGroupInfo[] | undefined,
  field: PriceTypeField,
): string | undefined {
  return sectionGroups?.find((group) => group.kind === PRICE_GROUP_KIND_BY_FIELD[field])?.id;
}

export function resolvePriceGroupIdFromSections(
  priceSections: Record<string, SectionItem[]>,
  field: PriceTypeField,
): string | undefined {
  for (const groupId of Object.keys(priceSections)) {
    if (priceSections[groupId]?.some((section) => section.type === PRODUCT_PRICE_SECTION_TYPE_BY_FIELD[field])) {
      return groupId;
    }
  }
  return undefined;
}

export function getNotesTabLabel(type: NotesType | undefined, fallbackLabel: string): string {
  if (isPublicNotesType(type) || isPrivateNotesType(type)) return $t("common.label.note");
  return fallbackLabel;
}

export function getFilesTabLabel(
  type: FileFolderType | undefined,
  fallbackLabel: string,
  isPriceGroup = false,
): string {
  if (type === FileFolderType.DEFAULT) return $t("common.label.files");
  if (type === FileFolderType.CUSTOM && isPriceGroup) return $t("common.label.files");
  if (type === FileFolderType.TEMPLATES) return $t("common.label.templates");
  if (type === FileFolderType.ARCHIVE) return $t("common.label.archive");
  return fallbackLabel;
}

export function getDefaultBookingsColumnSettings(): ColumnSetting[] {
  const visible = new Set(DEFAULT_ACCOUNTING_BOOKING_VISIBLE_COLUMNS);
  return DEFAULT_ACCOUNTING_BOOKING_COLUMN_ORDER.map((fieldSlug, idx) => ({
    fieldSlug,
    isVisible: visible.has(fieldSlug),
    width: ACCOUNTING_BOOKING_COLUMN_DEFAULT_WIDTHS[fieldSlug],
    order: idx,
  }));
}

export const DEFAULT_BOOKINGS_SHOW_FILTER_ROW = true;

export const DEFAULT_PRODUCT_PRICE_SHOW_FILTER_ROW = true;

export function getDefaultBookingsSettings(): FieldBlockSettingsBookingsInput {
  return newBookingsFieldBlockSettingsInput(getDefaultBookingsColumnSettings(), DEFAULT_BOOKINGS_SHOW_FILTER_ROW);
}

export function getDefaultNotesFieldCollection(): FieldBlockForm[] {
  return [
    {
      columns: [],
      ordering: 0,
      type: FieldBlockType.NOTES,
      localKey: crypto.randomUUID(),
      tab: { ordering: 0, label: $t("common.label.note") },
      settings: newNotesFieldBlockSettingsInput(NotesType.PUBLIC),
    },
    {
      columns: [],
      ordering: 1,
      type: FieldBlockType.NOTES,
      localKey: crypto.randomUUID(),
      tab: { ordering: 1, label: $t("common.label.note") },
      settings: newNotesFieldBlockSettingsInput(NotesType.PRIVATE),
    },
  ];
}

/**
 * Notes for a price section. Uses the CUSTOM_* variants (keyed per field block) so the content is
 * shared across the price-type tabs of one product but stays separate from the main tab's notes —
 * the notes counterpart of `getDefaultPriceFilesFieldCollection`.
 */
export function getDefaultPriceNotesFieldCollection(): FieldBlockForm[] {
  return [
    {
      columns: [],
      ordering: 0,
      type: FieldBlockType.NOTES,
      localKey: "price-notes-public",
      tab: { ordering: 0, label: $t("common.label.note") },
      settings: newNotesFieldBlockSettingsInput(NotesType.CUSTOM_PUBLIC),
    },
    {
      columns: [],
      ordering: 1,
      type: FieldBlockType.NOTES,
      localKey: "price-notes-private",
      tab: { ordering: 1, label: $t("common.label.note") },
      settings: newNotesFieldBlockSettingsInput(NotesType.CUSTOM_PRIVATE),
    },
  ];
}

export function getDefaultBookingsFieldCollection(): FieldBlockForm[] {
  return [
    {
      type: FieldBlockType.BOOKINGS,
      columns: [],
      ordering: 0,
      settings: getDefaultBookingsSettings(),
    },
  ];
}

export function getDefaultFilesFieldCollection(): FieldBlockForm[] {
  return [
    {
      columns: [],
      ordering: 0,
      type: FieldBlockType.FOLDERS,
      localKey: crypto.randomUUID(),
      tab: { ordering: 0, label: $t("common.label.files") },
      settings: newFoldersFieldBlockSettingsInput(FileFolderType.DEFAULT),
    },
    {
      columns: [],
      ordering: 1,
      type: FieldBlockType.FOLDERS,
      localKey: crypto.randomUUID(),
      tab: { ordering: 1, label: $t("common.label.templates") },
      settings: newFoldersFieldBlockSettingsInput(FileFolderType.TEMPLATES),
    },
    {
      columns: [],
      ordering: 2,
      type: FieldBlockType.FOLDERS,
      localKey: crypto.randomUUID(),
      tab: { ordering: 2, label: $t("common.label.archive") },
      settings: newFoldersFieldBlockSettingsInput(FileFolderType.ARCHIVE),
    },
  ];
}

/**
 * Files of a price tab must not be the record's files: a `DEFAULT` folder block always resolves to
 * the record root folder, so every tab (and the details tab) would show the same content. A
 * `CUSTOM` block gets a folder of its own per field block — see RelatedDataFileFoldersDTO. The
 * `TEMPLATES` block mirrors the record's own files section, so a price tab also has its templates.
 * `ARCHIVE` resolves like `CUSTOM`.
 */
export function getDefaultPriceFilesFieldCollection(): FieldBlockForm[] {
  return [
    {
      columns: [],
      ordering: 0,
      type: FieldBlockType.FOLDERS,
      localKey: "price-files-custom",
      tab: { ordering: 0, label: $t("common.label.files") },
      settings: newFoldersFieldBlockSettingsInput(FileFolderType.CUSTOM),
    },
    {
      columns: [],
      ordering: 1,
      type: FieldBlockType.FOLDERS,
      localKey: "price-files-templates",
      tab: { ordering: 1, label: $t("common.label.templates") },
      settings: newFoldersFieldBlockSettingsInput(FileFolderType.TEMPLATES),
    },
    {
      columns: [],
      ordering: 2,
      type: FieldBlockType.FOLDERS,
      localKey: "price-files-archive",
      tab: { ordering: 2, label: $t("common.label.archive") },
      settings: newFoldersFieldBlockSettingsInput(FileFolderType.ARCHIVE),
    },
  ];
}

export function getDefaultProductBodyFieldCollections(): FieldBlockForm[] {
  return [
    {
      columns: [],
      bands: [{ ordering: 0, columns: [{ rows: [{ fields: [], ordering: 0 }], ordering: 0 }] }],
      ordering: 0,
      localKey: crypto.randomUUID(),
      tab: { label: $t("common.label.details"), ordering: 0 },
    },
    {
      columns: [],
      bands: [{ ordering: 0, columns: [{ rows: [], ordering: 0 }] }],
      ordering: 1,
      localKey: crypto.randomUUID(),
      tab: { label: $t("common.label.accounting"), ordering: 1 },
    },
  ];
}

export type ProductAccountingPriceFlags = Partial<Record<PriceTypeField, boolean>>;

interface ProductAccountingBlockDefinition {
  field: PriceTypeField;
  slugs: readonly string[];
  label: () => string;
}

export const PRODUCT_ACCOUNTING_BLOCKS: ProductAccountingBlockDefinition[] = PRICE_TYPES.map((meta) => ({
  field: meta.field,
  slugs: PRODUCT_ACCOUNTING_SLUGS_BY_PRICE_TYPE[meta.field],
  label: () => $t(meta.labelKey),
}));

function buildProductAccountingFieldProxy(field: Field): FieldProxyForm {
  return {
    fieldId: field.id,
    field,
    slug: field.defaultSlug,
    label: field.label,
    required: false,
    hideLabel: false,
    prominent: false,
  } as FieldProxyForm;
}

interface AccountingRow {
  id?: string;
  updatedAt?: string;
  ordering?: number;
  label?: string;
  type?: LayoutRowType;
  fields: { slug: string; field?: { defaultSlug?: string } }[];
}

interface RowsCarryingBand {
  columns: { rows?: AccountingRow[] }[];
}

function accountingSlugOf(field: AccountingRow["fields"][number]): string {
  return field.field?.defaultSlug ?? field.slug;
}

function getCollectionRows(fc: FieldBlockForm): AccountingRow[] {
  if (fc.bands?.length) {
    return (fc.bands as unknown as RowsCarryingBand[]).flatMap((band) =>
      band.columns.flatMap((column) => column.rows ?? []),
    );
  }
  return (fc.columns as unknown as { rows?: AccountingRow[] }[]).flatMap((column) => column.rows ?? []);
}

function getBandRows(band: BandForm): AccountingRow[] {
  return band.columns.flatMap((col) => (col.rows ?? []) as unknown as AccountingRow[]);
}

function ensureAccountingBand(fc: FieldBlockForm): BandForm {
  const fcAsBanded = fc as unknown as { bands?: BandForm[] };
  if (!fcAsBanded.bands?.length) {
    fcAsBanded.bands = [{ ordering: 0, columns: [] }];
  }
  return fcAsBanded.bands[0];
}

function isEmptyAccountingRow(row: AccountingRow): boolean {
  return row.type !== LayoutRowType.HEADLINE && !row.fields?.length;
}

function isProductAccountingTabCollection(fc: FieldBlockForm): boolean {
  return getCollectionRows(fc).some((row) =>
    row.fields?.some((f) => PRODUCT_ACCOUNTING_FIELD_SLUGS.includes(accountingSlugOf(f))),
  );
}

function normalizeAccountingCollections(collections: FieldBlockForm[]): FieldBlockForm[] {
  return collections.map((fc, idx) => ({
    ...fc,
    ordering: idx,
    tab: fc.tab ? { ...fc.tab, ordering: idx } : fc.tab,
  }));
}

export function syncProductAccountingTab(
  sections: SectionItem[],
  priceFlags: ProductAccountingPriceFlags,
  fieldsBySlug: Map<string, Field>,
): void {
  const bodySection = sections.find((s) => s.type === LayoutSectionType.BODY);
  if (!bodySection) return;

  const collections = [...(bodySection.fieldCollections ?? [])] as FieldBlockForm[];
  const hasEnabledBlocks = PRODUCT_ACCOUNTING_BLOCKS.some((block) => !!priceFlags[block.field]);

  let accountingIndex = collections.findIndex((fc) => isProductAccountingTabCollection(fc));
  if (accountingIndex === -1) {
    accountingIndex = collections.findIndex((fc) => fc.tab?.label === $t("common.label.accounting"));
  }

  if (!hasEnabledBlocks) {
    if (accountingIndex === -1) return;
    collections.splice(accountingIndex, 1);
    bodySection.fieldCollections = normalizeAccountingCollections(collections) as never;
    return;
  }

  let accountingCollection: FieldBlockForm;
  if (accountingIndex === -1) {
    accountingCollection = {
      columns: [],
      bands: [{ ordering: 0, columns: [{ rows: [], ordering: 0 }] }],
      ordering: collections.length,
      localKey: crypto.randomUUID(),
      tab: { label: $t("common.label.accounting"), ordering: collections.length },
    } as unknown as FieldBlockForm;
    collections.push(accountingCollection);
  } else {
    accountingCollection = collections[accountingIndex];
  }

  const band = ensureAccountingBand(accountingCollection);

  const existingGroupRows = getBandRows(band).filter((row) => row.type === LayoutRowType.GROUP);
  const rowByField = new Map<PriceTypeField, AccountingRow>();

  const ownedRows = new Set<AccountingRow>();
  for (const block of PRODUCT_ACCOUNTING_BLOCKS) {
    const existing = existingGroupRows.find((row) => row.fields?.some((f) => accountingSlugOf(f) === block.slugs[0]));
    if (existing) {
      rowByField.set(block.field, existing);
      ownedRows.add(existing);
    }
  }

  const desiredRows = new Set<AccountingRow>();
  const newRowsByColumn: AccountingRow[][] = [[], []];
  PRODUCT_ACCOUNTING_BLOCKS.forEach((block, blockIndex) => {
    if (!priceFlags[block.field]) return;
    let row = rowByField.get(block.field);
    const existingSlugs = new Set(row?.fields.map(accountingSlugOf) ?? []);
    const missingProxies = block.slugs
      .filter((slug) => !existingSlugs.has(slug))
      .map((slug) => fieldsBySlug.get(slug))
      .filter((field): field is Field => !!field)
      .map(buildProductAccountingFieldProxy);
    if (!row) {
      if (missingProxies.length === 0) return;
      row = { type: LayoutRowType.GROUP, label: block.label(), fields: [], ordering: 0 };
      newRowsByColumn[blockIndex % 2].push(row);
    }
    if (missingProxies.length) row.fields = [...missingProxies, ...row.fields];
    desiredRows.add(row);
  });

  const storedColumns = (band.columns ?? []) as unknown as {
    span?: number;
    ordering?: number;
    rows?: AccountingRow[];
  }[];
  const columnCount = Math.max(storedColumns.length, newRowsByColumn.length);
  const mergedColumns = [];
  for (let colIndex = 0; colIndex < columnCount; colIndex++) {
    const column = storedColumns[colIndex] ?? { span: 1, rows: [] };
    const kept = (column.rows ?? []).filter((row) => !ownedRows.has(row) || desiredRows.has(row));
    const merged = [...kept, ...(newRowsByColumn[colIndex] ?? [])];
    const rows = merged.some((row) => !isEmptyAccountingRow(row))
      ? merged.filter((row) => !isEmptyAccountingRow(row))
      : merged.slice(0, 1);
    rows.forEach((row, rowIdx) => {
      row.ordering = rowIdx;
    });
    column.rows = rows;
    column.span ??= 1;
    column.ordering = colIndex;
    mergedColumns.push(column);
  }
  band.columns = mergedColumns as never;

  bodySection.fieldCollections = normalizeAccountingCollections(collections) as never;
}

export function ensureHeadSectionCollections(sections: SectionItem[]) {
  let headSection = sections.find((s) => s.type === LayoutSectionType.HEAD);
  if (!headSection) {
    headSection = {
      label: "Header",
      fieldCollections: [
        {
          columns: [],
          bands: [{ columns: [{ rows: [{ fields: [], ordering: 0 }], ordering: 0 }], ordering: 0 }],
          ordering: 0,
        },
      ],
      inSidebar: false,
      simple: false,
      ordering: 0,
      type: LayoutSectionType.HEAD,
      alignVertical: LayoutAlignVertical.TOP,
      labelSize: 52,
      status: LayoutSectionStatus.ACTIVE,
    };
    sections.unshift(headSection);
    sections.forEach((section, index) => {
      section.ordering = index;
    });
  }
}

function getPublicPrivateNotesTabs(fieldCollections: SectionItem["fieldCollections"]): SectionItem["fieldCollections"] {
  return fieldCollections.filter((fc) => {
    const type = (fc.settings as { type?: string } | null | undefined)?.type;
    return type === NotesType.PUBLIC || type === NotesType.PRIVATE;
  });
}

export function ensureNotesFieldCollections(sections: SectionItem[], sourceSections?: SectionItem[]) {
  let notesSection = sections.find((s) => s.type === LayoutSectionType.NOTES);
  if (!notesSection) {
    const sourceNotesSection = sourceSections?.find((s) => s.type === LayoutSectionType.NOTES);
    const sharedTabs = sourceNotesSection?.fieldCollections
      ? getPublicPrivateNotesTabs(sourceNotesSection.fieldCollections)
      : undefined;
    notesSection = {
      type: LayoutSectionType.NOTES,
      status: LayoutSectionStatus.ACTIVE,
      ordering: getNextSectionOrdering(sections),
      inSidebar: false,
      simple: false,
      label: getTemplateSectionLabel(LayoutSectionType.NOTES),
      labelSize: 128,
      fieldCollections: sharedTabs?.length ? sharedTabs : getDefaultNotesFieldCollection(),
    };
    sections.push(notesSection);
  }
}

export function ensureFieldCollections({
  sections,
  isDossier = false,
  hasRelations = false,
  sourceSections,
}: {
  sections: SectionItem[];
  isDossier: boolean;
  hasRelations: boolean;
  sourceSections?: SectionItem[];
}) {
  ensureHeadSectionCollections(sections);
  ensureNotesFieldCollections(sections, sourceSections);
  if (isDossier) {
    ensureBookingsFieldCollections(sections);
  }
  ensureFilesFieldCollections(sections, sourceSections);
  if (hasRelations) {
    ensureRelationsFieldCollections(sections);
  }
}

function extractBookingsColumns(section: LayoutSectionForm): ColumnSetting[] {
  const fc = section.fieldCollections?.[0];
  const columns = (fc?.settings as FieldBlockSettingsBookings | undefined)?.columns;
  return Array.isArray(columns) ? columns : [];
}

export function mergeBookingColumns(existing: ColumnSetting[]): ColumnSetting[] {
  const bySlug = new Map(existing.map((c) => [c.fieldSlug, c]));
  const hasMissing = DEFAULT_ACCOUNTING_BOOKING_COLUMN_ORDER.some((slug) => !bySlug.has(slug));
  if (!hasMissing) return existing;

  const visibleDefaults = new Set(DEFAULT_ACCOUNTING_BOOKING_VISIBLE_COLUMNS);

  return DEFAULT_ACCOUNTING_BOOKING_COLUMN_ORDER.map((slug, idx) => {
    const found = bySlug.get(slug);
    if (found) {
      return { ...found, order: idx };
    }
    return {
      fieldSlug: slug,
      isVisible: visibleDefaults.has(slug),
      width: ACCOUNTING_BOOKING_COLUMN_DEFAULT_WIDTHS[slug],
      order: idx,
    };
  });
}

export function ensureBookingsFieldCollections(sections: SectionItem[]) {
  const bookingsSection = sections.find((s) => s.type === LayoutSectionType.BOOKINGS);

  if (!bookingsSection) {
    sections.push({
      type: LayoutSectionType.BOOKINGS,
      status: LayoutSectionStatus.HIDDEN,
      ordering: getNextSectionOrdering(sections),
      inSidebar: false,
      simple: false,
      label: getTemplateSectionLabel(LayoutSectionType.BOOKINGS),
      labelSize: 128,
      fieldCollections: getDefaultBookingsFieldCollection(),
    });
    return;
  }

  // ensure all supported columns are present
  const fc = bookingsSection.fieldCollections?.[0];
  if (!fc) return;

  const existing = extractBookingsColumns(bookingsSection);
  const merged = mergeBookingColumns(existing);
  if (merged === existing) return;

  const showFilterRow = (fc.settings as FieldBlockSettingsBookings | undefined)?.showFilterRow;
  fc.settings = newBookingsFieldBlockSettingsInput(
    merged,
    typeof showFilterRow === "boolean" ? showFilterRow : DEFAULT_BOOKINGS_SHOW_FILTER_ROW,
  );
}

export function ensureFilesFieldCollections(sections: SectionItem[], sourceSections?: SectionItem[]) {
  const filesSection = sections.find((s) => s.type === LayoutSectionType.FILES);
  if (!filesSection) {
    const sourceFilesSection = sourceSections?.find((s) => s.type === LayoutSectionType.FILES);
    sections.push({
      type: LayoutSectionType.FILES,
      status: LayoutSectionStatus.ACTIVE,
      ordering: getNextSectionOrdering(sections),
      inSidebar: false,
      simple: false,
      label: getTemplateSectionLabel(LayoutSectionType.FILES),
      labelSize: 128,
      fieldCollections: sourceFilesSection?.fieldCollections ?? getDefaultFilesFieldCollection(),
    });
    return;
  }

  const getFolderType = (fc: FieldBlockForm) => (fc.settings as { type?: string } | null | undefined)?.type;
  const collections = Array.isArray(filesSection.fieldCollections) ? [...filesSection.fieldCollections] : [];

  const defaults = getDefaultFilesFieldCollection();
  const missing = defaults.filter((def) => !collections.some((fc) => getFolderType(fc) === getFolderType(def)));
  if (missing.length === 0) return;
  for (const def of missing) collections.push(def);

  // Canonical order is the defaults' own; a hand-added tab keeps its place after them.
  const order = defaults.map(getFolderType);
  filesSection.fieldCollections = [
    ...order.flatMap((type) => collections.filter((fc) => getFolderType(fc) === type)),
    ...collections.filter((fc) => !order.includes(getFolderType(fc))),
  ].map((fc, idx) => ({ ...fc, ordering: idx, tab: fc.tab ? { ...fc.tab, ordering: idx } : fc.tab }));
}

export function ensureRelationsFieldCollections(sections: SectionItem[]) {
  const relationsSection = sections.find((s) => s.type === LayoutSectionType.RELATIONS);
  if (!relationsSection) {
    sections.push({
      type: LayoutSectionType.RELATIONS,
      status: LayoutSectionStatus.ACTIVE,
      ordering: getNextSectionOrdering(sections),
      inSidebar: false,
      simple: false,
      label: $t("common.label.dossierConnections"),
      labelSize: 128,
      fieldCollections: [],
    });
  }
}

export function getNextSectionOrdering(sections: LayoutSectionForm[]): number {
  return sections.length === 0 ? 0 : Math.max(...(sections as { ordering: number }[]).map((s) => s.ordering), 0) + 1;
}

export function isSectionHidden(section: SectionItem | undefined): boolean {
  return section?.status === LayoutSectionStatus.HIDDEN;
}

function setSectionStatusByType(sections: SectionItem[], type: LayoutSectionType, status: LayoutSectionStatus): void {
  const section = sections?.find((s) => s.type === type);
  if (section) section.status = status;
}

export function enableSection(sections: SectionItem[], section: SectionItem): void {
  const exists = sections?.includes(section) ?? false;

  if (exists) {
    section.status = LayoutSectionStatus.ACTIVE;
    return;
  }

  if (!section.type) return;
  const sectionType: LayoutSectionType = section.type;

  if (sectionType === LayoutSectionType.NOTES) {
    ensureNotesFieldCollections(sections);
  } else if (sectionType === LayoutSectionType.BOOKINGS) {
    ensureBookingsFieldCollections(sections);
  } else if (sectionType === LayoutSectionType.FILES) {
    ensureFilesFieldCollections(sections);
  }
  setSectionStatusByType(sections, sectionType, LayoutSectionStatus.ACTIVE);
}

export function setProductPriceEnabled(templateModel: TemplateForm, field: PriceTypeField, enabled: boolean): void {
  templateModel[field] = enabled;
}

export function disableSection(section: SectionItem): void {
  section.status = LayoutSectionStatus.HIDDEN;
}

function confirmDeleteTabDialogs(modalStore: ReturnType<typeof useModalStore>, hasData: boolean): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    modalStore
      .push(
        SCAConfirmationDialog,
        {
          type: "delete",
          title: $t("common.action.deleteTab"),
          text: $t("common.action.deleteContent"),
          showDeleteSecondaryText: false,
        },
        {},
        { closeOnOverlayClick: true },
      )
      .onClose<"confirm" | "cancel" | "abort">((result) => {
        if (result !== "confirm") {
          resolve(false);
          return;
        }
        if (!hasData) {
          resolve(true);
          return;
        }
        modalStore
          .push(
            SCAConfirmationDialog,
            {
              type: "delete",
              title: $t("common.action.deletePermanently2"),
              text: $t("common.info.contentExistingRecords"),
              confirmationText: $t("common.action.deletePermanently"),
              showDeleteSecondaryText: false,
            },
            {},
            { closeOnOverlayClick: true },
          )
          .onClose<"confirm" | "cancel" | "abort">((result2) => {
            resolve(result2 === "confirm");
          });
      });
  });
}

export function useTabDeletionConfirmation(hasData?: (index: number) => boolean) {
  const modalStore = useModalStore();
  const beforeRemoveTab = (index: number) => confirmDeleteTabDialogs(modalStore, hasData ? hasData(index) : true);
  return { beforeRemoveTab };
}

function confirmHideSectionDialog(modalStore: ReturnType<typeof useModalStore>): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    modalStore
      .push(
        SCAConfirmationDialog,
        {
          title: $t("common.action.hideSection"),
          text: $t("settings.message.confirmHideSection"),
          type: "delete",
          confirmationText: $t("common.action.hide"),
          confirmIcon: "phosphor/eye-slash",
          showDeleteSecondaryText: false,
        },
        {},
        { closeOnOverlayClick: true },
      )
      .onClose<"confirm" | "cancel" | "abort">((result) => {
        resolve(!!result && result !== "abort" && result !== "cancel");
      });
  });
}

function confirmDeleteSectionDialog(modalStore: ReturnType<typeof useModalStore>): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    modalStore
      .push(
        SCAConfirmationDialog,
        {
          title: $t("common.action.deleteBody"),
          text: $t("settings.message.confirmDeleteBodySection"),
          type: "delete",
          confirmationText: $t("common.action.delete"),
          confirmIcon: "phosphor/trash",
          showDeleteSecondaryText: false,
        },
        {},
        { closeOnOverlayClick: true },
      )
      .onClose<"confirm" | "cancel" | "abort">((result) => {
        resolve(!!result && result !== "abort" && result !== "cancel");
      });
  });
}

export function useSectionConfirmations() {
  const modalStore = useModalStore();
  return {
    confirmHideSection: () => confirmHideSectionDialog(modalStore),
    confirmDeleteSection: () => confirmDeleteSectionDialog(modalStore),
  };
}

export function confirmDisableBookingsDialog(modalStore: ReturnType<typeof useModalStore>): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    modalStore
      .push(
        SCAConfirmationDialog,
        {
          title: $t("common.action.disableAccounting"),
          text: $t("settings.message.confirmDisableAccounting"),
          type: "delete",
          confirmationText: $t("common.action.hide"),
          confirmIcon: "phosphor/eye-slash",
          showDeleteSecondaryText: false,
        },
        {},
        { closeOnOverlayClick: true },
      )
      .onClose<"confirm" | "cancel" | "abort">((result) => {
        resolve(!!result && result !== "abort" && result !== "cancel");
      });
  });
}

export async function toggleBookingsSection(
  sections: SectionItem[],
  modalStore: ReturnType<typeof useModalStore>,
): Promise<void> {
  const bookingsSection = sections.find((s) => s.type === LayoutSectionType.BOOKINGS);
  if (!bookingsSection) return;

  const isEnabled = bookingsSection.status === LayoutSectionStatus.ACTIVE;

  if (isEnabled) {
    const confirmed = await confirmDisableBookingsDialog(modalStore);
    if (!confirmed) return;
    disableSection(bookingsSection);
  } else {
    enableSection(sections, bookingsSection);
  }
}

export function useTabCollections(sectionModel: Ref<SectionItem>) {
  const selectedTabIndex = ref(0);
  let suppressSelectedIndexUpdate = false;

  function onSelectedIndexUpdate(newIndex: number) {
    if (suppressSelectedIndexUpdate) {
      suppressSelectedIndexUpdate = false;
      return;
    }
    selectedTabIndex.value = newIndex;
  }

  function getSnapshotKey(fc: FieldBlockForm): string {
    if ("id" in fc && fc.id) return fc.id;
    if (fc.localKey) return fc.localKey;
    return `${fc.tab?.label ?? ""}:${fc.ordering ?? 0}`;
  }

  function applyTabUpdate(
    sortedCollections: FieldBlockForm[],
    newTabs: TabItem[],
    makeNewCollection: (tab: TabItem, index: number) => FieldBlockForm,
    postProcess?: (cols: FieldBlockForm[]) => FieldBlockForm[],
  ) {
    const selectedKey = sortedCollections[selectedTabIndex.value]
      ? getSnapshotKey(sortedCollections[selectedTabIndex.value])
      : null;

    const currentCols = Array.isArray(sectionModel.value?.fieldCollections)
      ? [...sectionModel.value.fieldCollections]
      : [];

    const colMap = new Map(currentCols.map((fc) => [getSnapshotKey(fc), fc]));

    let updatedCols: FieldBlockForm[] = newTabs.map((tab, index) => {
      const existing = colMap.get(tab.key);

      if (existing) {
        return {
          ...existing,
          ordering: index,
          tab: { ...existing.tab, label: tab.label, ordering: index },
        } as FieldBlockForm;
      } else {
        return makeNewCollection(tab, index);
      }
    });

    if (postProcess) {
      updatedCols = postProcess(updatedCols);
    }

    sectionModel.value = {
      ...sectionModel.value,
      fieldCollections: updatedCols as never,
    };

    const newTabFc = updatedCols.find((fc) => !colMap.has(getSnapshotKey(fc)));
    if (newTabFc) {
      const newIdx = updatedCols.indexOf(newTabFc);
      if (newIdx >= 0) selectedTabIndex.value = newIdx;
    } else if (selectedKey) {
      const newIdx = updatedCols.findIndex((fc) => getSnapshotKey(fc) === selectedKey);
      if (newIdx >= 0) selectedTabIndex.value = newIdx;
    }

    if (selectedTabIndex.value >= updatedCols.length) {
      selectedTabIndex.value = Math.max(0, updatedCols.length - 1);
    }

    suppressSelectedIndexUpdate = true;
  }

  return { selectedTabIndex, onSelectedIndexUpdate, getSnapshotKey, applyTabUpdate };
}

export function sortByOrdering<T extends { ordering?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0));
}

function sortColumns(columns: LayoutColumn[]): LayoutColumn[] {
  return sortByOrdering(columns).map((column) => ({
    ...column,
    rows: sortByOrdering(column.rows).map((row) => ({
      ...row,
      fields: row.fields.map((field) => ({
        ...field,
        fieldGroup: field.fieldGroup
          ? {
              ...field.fieldGroup,
              rows: sortByOrdering(field.fieldGroup.rows ?? []).map((groupRow) => ({
                ...groupRow,
                fields: groupRow.fields ?? [],
              })),
            }
          : field.fieldGroup,
      })) as FieldProxy[],
    })),
  })) as LayoutColumn[];
}

export const sortLayoutSections = (sections: LayoutSection[]): LayoutSection[] => {
  return sortByOrdering(sections).map((section) => ({
    ...section,
    fieldCollections: sortByOrdering(section.fieldCollections).map((collection) => ({
      ...collection,
      columns: sortColumns(collection.columns),
      bands: sortByOrdering(collection.bands ?? []).map((band) => ({
        ...band,
        columns: sortColumns(band.columns),
      })),
    })) as FieldBlock[],
  })) as LayoutSection[];
};

export const sortTemplateData = (template: TemplateOutput | undefined) => {
  if (!template) return template;

  return {
    ...template,
    sections: sortLayoutSections(template.sections),
  };
};
